<template>
  <div :id="`order_kpi_appeal_${type}`">
    <b-modal :active.sync="appealModal.active" width="960px" has-modal-card>
      <order-kpi-appeal-modal
        :order-id="order.id"
        :start-or-finish="appealModal.startOrFinish"
        :mode="appealModal.mode"
        :subtitle="appealModal.subtitle"
        @submit="onAppealModalSubmit"
      />
    </b-modal>
    <div class="columns is-vcentered is-variable is-1">
      <div class="column is-6">
        <b-button
          :type="kpi | kpiType"
          class="is-fullwidth has-text-weight-bold"
          :disabled="!order.editable"
          v-if="kpi"
        >
          {{ kpi | uppercase }}
        </b-button>
      </div>
      <div class="column is-6">
        <div v-if="!appealStatus">
          <b-button
            :id="`order_kpi_actual_${type}_appeal`"
            class="is-fullwidth has-text-weight-bold"
            :disabled="!order.editable"
            @click="appeal"
            v-if="canAppeal && !canOverruleAppeal"
          >
            APPEAL
          </b-button>
        </div>
        <div class="buttons" v-if="appealStatus">
          <b-tooltip
            :id="`order_kpi_appeal_${type}_pending`"
            :label="hoverText"
            type="is-dark"
            multilined
            style="width:100%"
            @mouseover.native="hovering = true"
            @mouseleave.native="hovering = false"
            v-if="
              appealStatus === $options.statuses.pending && canOverruleAppeal
            "
          >
            <b-field style="width:100%" v-if="hovering">
              <p class="control" style="width:50%">
                <b-button
                  :id="`order_kpi_actual_${type}_approve`"
                  class="is-fullwidth"
                  type="is-success"
                  icon-right="check-circle"
                  :loading="approving"
                  :disabled="!order.editable"
                  @click="approve"
                  v-if="canOverruleAppeal"
                />
              </p>
              <p class="control" style="width:50%">
                <b-button
                  class="is-fullwidth"
                  type="is-danger"
                  icon-right="times-circle"
                  :disabled="!order.editable"
                  @click="reject"
                  v-if="canOverruleAppeal"
                />
              </p>
            </b-field>
            <b-button
              :type="appealStatus | appealStatusType"
              :icon-left="hoverText ? 'info-circle' : ''"
              class="is-fullwidth kpi-btn ws-normal"
              v-else
            >
              <p class="kpi-btn-text">
                {{ `Appeal ${appealStatus}` | uppercase }}
              </p>
            </b-button>
          </b-tooltip>
          <b-tooltip
            :label="hoverText"
            type="is-dark"
            multilined
            style="width:100%"
            v-else
          >
            <b-button
              :type="appealStatus | appealStatusType"
              :icon-left="hoverText ? 'info-circle' : ''"
              class="is-fullwidth kpi-btn ws-normal"
              :disabled="!order.editable"
            >
              <p class="kpi-btn-text">
                {{ `Appeal ${appealStatus}` | uppercase }}
              </p>
            </b-button>
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderKpiAppealModal from "./OrderKpiAppealModal";
import { OrderService } from "@/services/order.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import { startCase } from "lodash";

export default {
  orderService: OrderService,
  statuses: {
    approved: "APPROVED",
    rejected: "REJECTED",
    pending: "PENDING"
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    type: {
      validator: value => {
        return ["start", "finish"].indexOf(value) !== -1;
      },
      required: true
    }
  },
  filters: {
    kpiType(kpi) {
      return kpi === "pass" ? "is-success" : "is-danger";
    },
    appealStatusType(status) {
      switch (status) {
        case "PENDING":
          return "is-warning";
        case "APPROVED":
          return "is-success";
        case "REJECTED":
          return "is-danger";
      }
      return "";
    }
  },
  components: {
    OrderKpiAppealModal
  },
  data: () => ({
    appealModal: {
      startOrFinish: "",
      active: false,
      type: "",
      result: "",
      mode: ""
    },
    approving: false,
    hovering: false
  }),
  computed: {
    kpiProperty() {
      return this.type === "start" ? "kpiStart" : "kpiFinish";
    },
    kpi() {
      return this.order[this.kpiProperty];
    },
    appealLogs() {
      const logs = this.order.kpiLogs || [];

      return logs.filter(
        log => !!log.subtype.match(`kpi_${this.type}`.toUpperCase())
      );
    },
    appealStatus() {
      if (this.appealLogs.length > 0) {
        const status = this.appealLogs[0].subtype.split(".")[1];

        switch (status) {
          case "APPROVE":
            return this.$options.statuses.approved;
          case "REJECT":
            return this.$options.statuses.rejected;
          case "APPEAL":
            return this.$options.statuses.pending;
          case "REVERSE":
            return this.$options.statuses.pending;
        }
      }
    },
    hoverText() {
      switch (this.appealStatus) {
        case this.$options.statuses.rejected:
        case this.$options.statuses.approved:
        case this.$options.statuses.pending:
          try {
            const data = JSON.parse(this.appealLogs[0].content);
            return Object.keys(data)
              .reduce((acc, key) => {
                let value = data[key];

                acc.push(`${startCase(key)}: ${value}`);

                return acc;
              }, [])
              .join(" | ");
          } catch (err) {
            // data is not a valid JSON
          }

        break;

        default: return 'No further information available.';
        
      }

      return "No further information available.";
    },
    canAppeal() {
      const can_state_item =
        this.type == "start" ? "can_appeal_kpi_start" : "can_appeal_kpi_finish";
      const can_by_user_state_item =
        this.type == "start"
          ? "can_appeal_kpi_start_by_user"
          : "can_appeal_kpi_finish_by_user";
      const obj1 = this.$store.getters["OrderState/getStateByKey"](
        can_state_item
      );
      const obj2 = this.$store.getters["OrderState/getStateByKey"](
        can_by_user_state_item
      );
      const outcome = obj1 && obj1.value && obj2 && obj2.value;

      return outcome;
    },
    // can approve/reject appeal
    canOverruleAppeal() {
      const can_state_item =
        this.type == "start"
          ? "can_overrule_kpi_start"
          : "can_overrule_kpi_finish";
      const can_by_user_state_item =
        this.type == "start"
          ? "can_overrule_kpi_start_by_user"
          : "can_overrule_kpi_finish_by_user";
      const obj1 = this.$store.getters["OrderState/getStateByKey"](
        can_state_item
      );
      const obj2 = this.$store.getters["OrderState/getStateByKey"](
        can_by_user_state_item
      );
      const outcome = obj1 && obj1.value && obj2 && obj2.value;

      return outcome;
    }
  },
  methods: {
    async approve() {
      this.approving = true;

      try {
        const order = await this.$options.orderService
          .approveKpiResult(this.order.id, {
            startOrFinish: this.type,
            includes: "kpiLogs"
          })
          .then(response => JsonDenormalizer.denormalize(response.data));

        this.$set(this.order, this.kpiProperty, order[this.kpiProperty]);
        this.$set(this.order, "kpiLogs", order.kpiLogs);
        this.$store.dispatch("OrderState/fetchState", this.order.id);

        this.$buefy.toast.open({
          message: "KPI has been approved.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}

      this.approving = false;
    },
    appeal() {
      this.appealModal.startOrFinish = this.type;
      this.appealModal.mode = this.kpi === "pass" ? "reverse" : "appeal";
      this.appealModal.subtitle = `for ${this.type} time`;
      this.appealModal.active = true;
    },
    reject() {
      this.appealModal.startOrFinish = this.type;
      this.appealModal.mode = "reject";
      this.appealModal.subtitle = `for ${this.type} time`;
      this.appealModal.active = true;
    },
    onAppealModalSubmit(order) {
      this.$set(this.order, "kpiLogs", order.kpiLogs);
      this.$store.dispatch("OrderState/fetchState", order.id);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/OrderKpi/OrderKpiAppeal.vue